import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { constants } from '../Constants'
import { PageLoader } from '../components/ui/Spinner/Spinner'
import './TableOfTrophy.scss'

const MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

const Avatar = ({ name, src, size = 'md' }) => {
  const initials = name
    ? name
        .split(' ')
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase()
    : '?'
  return (
    <div className={`rk-avatar rk-avatar--${size}`}>
      {src ? <img src={src} alt={name} /> : <span>{initials}</span>}
    </div>
  )
}

export const TableOfTrophy = () => {
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [filterByMonth, setFilterByMonth] = useState(false)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const localUser = JSON.parse(localStorage.getItem('user'))
  const myRef = useRef(null)

  const scrollToMe = () => {
    myRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const m = filterByMonth ? month : 0
        const res = await axios.get(
          `${constants.urlLocal}inscription/getTotalInscriptionsOfAllUser/${year}/${m}`
        )
        setData(res.data.inscriptions || [])
      } catch (e) {
        console.error(e)
      }
      setLoading(false)
    }
    fetchData()
  }, [year, month, filterByMonth])

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i)
  const top3 = data.slice(0, 3)
  const rest = data.slice(3)
  const max = data[0]?.totalInscriptions || 1

  // Orden del podio: 2º - 1º - 3º
  const podiumOrder = [top3[1], top3[0], top3[2]]
  const podiumHeights = [70, 100, 50]
  const podiumLabels = ['🥈', '🥇', '🥉']
  const podiumPos = [2, 1, 3]

  return (
    <div className="rk-page page">
      {/* Filtros */}
      <div className="rk-filters">
        <div className="rk-filters__group">
          <select
            className="rk-select"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {years.map(y => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <button
            className={`rk-toggle ${filterByMonth ? 'rk-toggle--active' : ''}`}
            onClick={() => setFilterByMonth(v => !v)}
          >
            {filterByMonth ? `${MONTHS[month - 1]}` : 'Todo el año'}
          </button>

          {filterByMonth && (
            <select
              className="rk-select"
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          )}
        </div>
        {!loading &&
          data.some(u => u.id?.toString() === localUser?.id?.toString()) && (
            <button className="rk-me-btn" onClick={scrollToMe}>
              🎯 Mi posición
            </button>
          )}
      </div>

      {loading ? (
        <PageLoader text="Cargando ranking..." />
      ) : data.length === 0 ? (
        <div className="rk-empty">
          <span>🏆</span>
          <p>Sin datos para este periodo</p>
        </div>
      ) : (
        <>
          {/* Podio */}
          {top3.length >= 2 && (
            <div className="rk-podium">
              {podiumOrder.map((user, idx) => {
                if (!user) return <div key={idx} className="rk-podium__slot" />
                const isMe = user.id?.toString() === localUser?.id?.toString()
                return (
                  <div
                    ref={isMe ? myRef : null}
                    key={user._id || idx}
                    className={`rk-podium__slot rk-podium__slot--${podiumPos[idx]}${isMe ? ' rk-podium__slot--me' : ''}`}
                  >
                    <div className="rk-podium__avatar-wrap">
                      <Avatar
                        name={`${user.name} ${user.lastName}`}
                        src={user.avatar}
                        size="lg"
                      />
                      <span className="rk-podium__medal">
                        {podiumLabels[idx]}
                      </span>
                    </div>
                    <span
                      className={`rk-podium__name${isMe ? ' rk-podium__name--me' : ''}`}
                    >
                      {user.name}
                      {isMe ? ' 👋🏽' : ''}
                    </span>
                    <span className="rk-podium__count">
                      {user.totalInscriptions}
                    </span>
                    <div
                      className="rk-podium__block"
                      style={{ height: `${podiumHeights[idx]}px` }}
                    >
                      <span className="rk-podium__pos">#{podiumPos[idx]}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Resto del ranking */}
          {rest.length > 0 && (
            <div className="rk-list">
              {rest.map((user, i) => {
                const pos = i + 4
                const pct = Math.round((user.totalInscriptions / max) * 100)
                const isMe = user.id?.toString() === localUser?.id?.toString()
                return (
                  <Link
                    key={user._id || i}
                    to={`/user/${user.id}`}
                    ref={isMe ? myRef : null}
                    className={`rk-row${isMe ? ' rk-row--me' : ''}`}
                  >
                    <span className="rk-row__pos">{pos}</span>
                    <Avatar
                      name={`${user.name} ${user.lastName}`}
                      src={user.avatar}
                      size="sm"
                    />
                    <div className="rk-row__info">
                      <span className="rk-row__name">
                        {user.name} {user.lastName}
                        {isMe && <span className="rk-row__me-badge"> Tú</span>}
                      </span>
                      <div className="rk-row__bar">
                        <div
                          className="rk-row__bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <span className="rk-row__count">
                      {user.totalInscriptions}
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
